import { DraggerType } from "./types";
import type { BaseDragger } from "./BaseDragger";
import { SequentialDragger } from "./SequentialDragger";
import type { DRDrawingViewer } from "../DRDrawingViewer";

/**
 * Manages dragger registration and activation.
 * Only one dragger can be active at a time.
 */
export class DraggerManager {
  private viewer: DRDrawingViewer;
  private draggers = new Map<DraggerType, BaseDragger>();
  private activeDragger: BaseDragger | null = null;
  private activeChangeHandlers: ((type: DraggerType) => void)[] = [];

  constructor(viewer: DRDrawingViewer) {
    this.viewer = viewer;
  }

  /**
   * Register a dragger for a given type.
   * Replaces any existing dragger of the same type.
   */
  register(type: DraggerType, dragger: BaseDragger): void {
    // Dispose existing dragger if present
    const existing = this.draggers.get(type);
    if (existing) {
      if (existing === this.activeDragger) {
        this.deactivate();
      }
      existing.dispose();
    }
    this.draggers.set(type, dragger);
  }

  /**
   * Unregister a dragger by type.
   */
  unregister(type: DraggerType): void {
    const dragger = this.draggers.get(type);
    if (dragger) {
      if (dragger === this.activeDragger) {
        this.deactivate();
      }
      dragger.dispose();
      this.draggers.delete(type);
    }
  }

  /**
   * Activate a dragger by type.
   * Deactivates the current dragger first.
   * @returns true if activation succeeded, false if dragger not found
   */
  activate(type: DraggerType): boolean {
    // Handle None type - just deactivate
    if (type === DraggerType.None) {
      this.deactivate();
      return true;
    }

    // Find the dragger
    const dragger = this.draggers.get(type);

    if (!dragger) {
      return false;
    }

    // Don't reactivate same dragger
    if (dragger === this.activeDragger) {
      return true;
    }

    // Deactivate current dragger
    if (this.activeDragger) {
      this.activeDragger.deactivate();
    }

    // Activate new dragger
    dragger.activate();
    this.activeDragger = dragger;
    this.notifyActiveChange();

    return true;
  }

  /**
   * Deactivate the current dragger.
   */
  deactivate(): void {
    if (this.activeDragger) {
      this.activeDragger.deactivate();
      this.activeDragger = null;
      this.notifyActiveChange();
    }
  }

  /**
   * Subscribe to active-tool changes. DraggerMenuState.activeDragger is not
   * enough on its own: the base-point edit tools (copy/move/rotate/scale) are
   * activated straight through this manager, and deliberately never touch the
   * menu state (doing so would clear the selection they operate on).
   * @returns unsubscribe function
   */
  onActiveChange(handler: (type: DraggerType) => void): () => void {
    this.activeChangeHandlers.push(handler);
    return () => {
      const index = this.activeChangeHandlers.indexOf(handler);
      if (index >= 0) {
        this.activeChangeHandlers.splice(index, 1);
      }
    };
  }

  private notifyActiveChange(): void {
    const type = this.currentType;
    for (const handler of this.activeChangeHandlers) {
      handler(type);
    }
  }

  /**
   * Get the currently active dragger.
   */
  get current(): BaseDragger | null {
    return this.activeDragger;
  }

  /**
   * Get the type of the currently active dragger.
   */
  get currentType(): DraggerType {
    return this.activeDragger?.type ?? DraggerType.None;
  }

  /**
   * Check if a specific dragger type is registered.
   */
  has(type: DraggerType): boolean {
    return this.draggers.has(type);
  }

  /**
   * Get all registered dragger types.
   */
  get registeredTypes(): DraggerType[] {
    return Array.from(this.draggers.keys());
  }

  /**
   * Set autoSelect on all registered draggers.
   * When enabled, entities created by draggers will be automatically selected.
   */
  setAutoSelect(enabled: boolean): void {
    for (const dragger of this.draggers.values()) {
      dragger.setAutoSelect(enabled);
    }
  }

  /**
   * Get the autoSelect setting from the first dragger (assumes all are the same).
   */
  getAutoSelect(): boolean {
    const firstDragger = this.draggers.values().next().value;
    return firstDragger?.autoSelect ?? false;
  }

  /**
   * Set two-point sequential placement mode on all registered draggers.
   * No-op on any dragger that doesn't support it (only DualModeDragger-based
   * draggers, e.g. Rectangle/Ellipse/Line, act on this).
   */
  setTwoPointMode(enabled: boolean): void {
    for (const dragger of this.draggers.values()) {
      dragger.setTwoPointMode(enabled);
    }
  }

  /**
   * Finish the current sequential drawing.
   * Creates an open shape with the points collected so far.
   * Has no effect if the current dragger is not a SequentialDragger.
   */
  finishDrawing(): void {
    if (this.activeDragger instanceof SequentialDragger) {
      this.activeDragger.finishDrawing();
    }
  }

  /**
   * Dispose all draggers and clean up.
   */
  dispose(): void {
    this.deactivate();
    for (const dragger of this.draggers.values()) {
      dragger.dispose();
    }
    this.draggers.clear();
    this.activeChangeHandlers = [];
  }
}
